import Navbar from "../components/Navbar";
import MatchCard from "../components/MatchCard";
import { useState, useEffect } from "react";
import { db } from "../firebase/firebaseConfig";
import { collection, onSnapshot, query, setDoc, doc } from "firebase/firestore";
import { matches as staticMatches } from "../data/matchData";

function Home() {
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('upcoming');

  useEffect(() => {
    const q = query(collection(db, "matches"));
    const unsub = onSnapshot(q, (snapshot) => {
      const matchData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      
      // Auto-sync: if DB is missing matches from the local list
      if (matchData.length < staticMatches.length) {
        seedMatches(matchData);
      } else {
        setMatches(matchData.sort((a, b) => parseInt(a.id) - parseInt(b.id)));
        setLoading(false);
      }
    });

    return () => unsub();
  }, []);

  const seedMatches = async (existingMatches) => {
    try {
      const existingIds = existingMatches.map(m => parseInt(m.id));
      console.log(`Checking and synchronizing missing matches...`);
      let added = 0;
      for (const m of staticMatches) {
        if (!existingIds.includes(parseInt(m.id))) {
          await setDoc(doc(db, "matches", m.id.toString()), { 
            ...m, 
            status: m.status || 'upcoming', 
            winner: m.winner || null 
          }); 
          added++;
        }
      }
      if (added > 0) {
        console.log(`Successfully added ${added} missing matches.`);
      }
    } catch (err) {
      console.error("Sync failed:", err);
    }
  };

  const filteredMatches = matches.filter(m => {
    if (filter === 'all') return true;
    if (filter === 'upcoming') return m.status === 'upcoming';
    if (filter === 'finished') return m.status === 'finished' || m.status === 'cancelled';
    return true;
  });

  return (
    <div style={{ minHeight: "100vh", paddingBottom: "3rem" }}>
      <Navbar />

      <main className="app-container">
        <header style={styles.heroSection}>
          <div style={styles.ballContainer} className="floating-ball">
            <div style={styles.cricketBall}></div>
          </div>

          <h1 className="gradient-text" style={styles.heroTitle}>IPL 2026 ARENA</h1>
          <p style={styles.heroSubtitle}>Predict. Win. Rule the Leaderboard.</p>

          <div className="glass-morphism" style={styles.statsStrip}>
            <div style={styles.statItem}>
              <span style={styles.statVal}>{matches.length}</span>
              <span style={styles.statLabel}>MATCHES</span>
            </div>
            <div style={styles.statDivider}></div>
            <div style={styles.statItem}>
              <span style={styles.statVal}>LIVE</span>
              <span style={styles.statLabel}>STATUS</span>
            </div>
          </div>
        </header>

        <div style={styles.filterMenu}>
           <button onClick={() => setFilter('upcoming')} style={filter === 'upcoming' ? styles.activeTab : styles.tab}>UPCOMING</button>
           <button onClick={() => setFilter('finished')} style={filter === 'finished' ? styles.activeTab : styles.tab}>RESULTS</button>
           <button onClick={() => setFilter('all')} style={filter === 'all' ? styles.activeTab : styles.tab}>FULL SCHEDULE</button>
        </div>

        {loading ? (
          <div style={styles.loader}>Accessing Arena Data...</div>
        ) : (
          <section className="match-grid">
            {filteredMatches.map((match) => (
              <MatchCard
                key={match.id}
                id={match.id}
                teamA={match.teamA}
                teamB={match.teamB}
                venue={match.venue}
                date={match.date}
                time={match.time}
                status={match.status}
                winner={match.winner}
                predictWindow={match.predictWindow}
                teamAScore={match.teamAScore}
                teamBScore={match.teamBScore}
                resultReason={match.resultReason}
              />
            ))}
          </section>
        )}
      </main>
    </div>
  );
}

const styles = {
  heroSection: { padding: '4rem 0', textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '1.5rem', position: 'relative' },
  heroTitle: { fontSize: '4.5rem', lineHeight: 1.1 },
  heroSubtitle: { fontSize: '1.2rem', color: 'var(--text-secondary)', letterSpacing: '0.1em', fontWeight: '600' },
  ballContainer: { marginBottom: '1rem' },
  cricketBall: { width: '60px', height: '60px', borderRadius: '50%', background: 'radial-gradient(circle at 30% 30%, #FF4B2B, #FF0055)', boxShadow: 'inset -5px -5px 15px rgba(0,0,0,0.5), 0 10px 20px rgba(255,0,85,0.3)', position: 'relative', border: '2px solid rgba(255,255,255,0.2)' },
  statsStrip: { display: 'flex', padding: '1.5rem 3rem', borderRadius: '100px', gap: '3rem', marginTop: '1rem', alignItems: 'center' },
  statItem: { display: 'flex', flexDirection: 'column', alignItems: 'center' },
  statVal: { fontSize: '1.5rem', fontWeight: '900', color: 'var(--primary)' },
  statLabel: { fontSize: '0.7rem', color: 'var(--text-secondary)', fontWeight: '700' },
  statDivider: { width: '1px', height: '30px', background: 'var(--glass-border)' },
  filterMenu: { display: 'flex', justifyContent: 'center', gap: '1rem', marginBottom: '2.5rem', flexWrap: 'wrap' },
  tab: { padding: '0.8rem 1.5rem', borderRadius: '50px', background: 'rgba(255,255,255,0.03)', border: '1px solid var(--glass-border)', color: 'var(--text-secondary)', fontWeight: '800', cursor: 'pointer', transition: '0.2s', fontSize: '0.8rem', letterSpacing: '0.05em' },
  activeTab: { padding: '0.8rem 1.5rem', borderRadius: '50px', background: 'var(--primary)', border: '1px solid var(--primary)', color: '#000', fontWeight: '900', cursor: 'pointer', transition: '0.2s', fontSize: '0.8rem', letterSpacing: '0.05em', boxShadow: '0 4px 15px rgba(0, 118, 255, 0.3)' },
  loader: { textAlign: 'center', color: 'var(--primary)', padding: '5rem', fontSize: '1.2rem', fontWeight: 'bold' }
};

export default Home;
